import { HttpRemoteStore } from '../providers'
import type { McpConnectionEntry } from './types'
import { buildConfigWorkspaceSecret } from './crypto'
import { type WorkspaceSecretV2, encodeWorkspaceSecretV2 } from '@secureblob/sdk/workspaceSecretV2'
import { createWorkspaceAuthProvider } from '@secureblob/sdk/workspaceAuth'
import { recordBrowserHttpErrorResponse } from '@secureblob/sdk/browserHttpErrors'
import { deriveMcpSeedIdentity } from './credentialIdentity'

const RATE_LIMIT_MAX_ATTEMPTS = 4
const RATE_LIMIT_RETRY_BASE_MS = 250

function getEphemeralHeaders(): Record<string, string> {
    const secret = typeof window !== 'undefined' && (window as any).__e2eStorageSecret
    if (secret) {
        return { 'X-Storage-Mode': `memory:${secret}` }
    }
    return {}
}

async function getManageToken(
    apiBaseUrl: string,
    managerSecret: WorkspaceSecretV2,
): Promise<string> {
    const provider = createWorkspaceAuthProvider(apiBaseUrl, encodeWorkspaceSecretV2(managerSecret), managerSecret.blobId)
    if (!provider) {
        throw new Error(`Failed to create workspace auth provider for '${managerSecret.blobId}'.`)
    }
    const token = await provider.getAccessToken('write')
    return token.token
}

function getRetryAfterMs(headerValue: string | null): number | null {
    if (!headerValue) return null

    const seconds = Number(headerValue)
    if (Number.isFinite(seconds)) {
        return Math.max(0, seconds * 1000)
    }

    const retryAt = Date.parse(headerValue)
    if (Number.isNaN(retryAt)) return null
    return Math.max(0, retryAt - Date.now())
}

function delay(ms: number): Promise<void> {
    if (ms <= 0) return Promise.resolve()
    return new Promise(resolve => setTimeout(resolve, ms))
}

async function fetchWithRateLimitRetry(
    input: string,
    init: RequestInit,
): Promise<Response> {
    for (let attempt = 1; attempt <= RATE_LIMIT_MAX_ATTEMPTS; attempt++) {
        const response = await fetch(input, init)
        if (response.status !== 429 || attempt >= RATE_LIMIT_MAX_ATTEMPTS) {
            recordBrowserHttpErrorResponse(response, {
                url: input,
                method: init.method,
            })
            return response
        }

        const retryAfterMs = getRetryAfterMs(response.headers.get('retry-after'))
            ?? RATE_LIMIT_RETRY_BASE_MS * (2 ** (attempt - 1))
        await delay(retryAfterMs)
    }

    throw new Error('ACL request failed: retries exhausted')
}

export async function grantMcpWorkspaceAccess(
    apiBaseUrl: string,
    managerSecret: WorkspaceSecretV2,
    readerSeed: string,
    writerSeed?: string,
): Promise<void> {
    const authorization = `Bearer ${await getManageToken(apiBaseUrl, managerSecret)}`
    const reader = await deriveMcpSeedIdentity(readerSeed)
    const writer = writerSeed ? await deriveMcpSeedIdentity(writerSeed) : null

    const response = await fetchWithRateLimitRetry(`${apiBaseUrl}/auth/manage/grant`, {
        method: 'POST',
        headers: {
            'content-type': 'application/json',
            authorization,
            ...getEphemeralHeaders(),
        },
        body: JSON.stringify({
            blobId: managerSecret.blobId,
            reader,
            ...(writer ? { writer } : {}),
        }),
    })

    if (!response.ok) {
        let detail = `ACL grant failed: ${response.status}`
        try {
            const body = await response.json() as { error?: { code?: string } }
            if (body.error?.code) detail = `ACL grant failed: ${body.error.code}`
        } catch {
            // use default
        }
        throw new Error(detail)
    }
}

export async function revokeMcpWorkspaceAccess(
    apiBaseUrl: string,
    managerSecret: WorkspaceSecretV2,
    readerSeed?: string,
    writerSeed?: string,
): Promise<void> {
    const authorization = `Bearer ${await getManageToken(apiBaseUrl, managerSecret)}`
    const readerKid = readerSeed ? (await deriveMcpSeedIdentity(readerSeed)).kid : null
    const writerKid = writerSeed ? (await deriveMcpSeedIdentity(writerSeed)).kid : null

    if (!readerKid && !writerKid) return

    const response = await fetchWithRateLimitRetry(`${apiBaseUrl}/auth/manage/revoke`, {
        method: 'POST',
        headers: {
            'content-type': 'application/json',
            authorization,
            ...getEphemeralHeaders(),
        },
        body: JSON.stringify({
            blobId: managerSecret.blobId,
            ...(readerKid ? { readerKid } : {}),
            ...(writerKid ? { writerKid } : {}),
        }),
    })

    if (!response.ok) {
        let detail = `ACL revoke failed: ${response.status}`
        try {
            const body = await response.json() as { error?: { code?: string } }
            if (body.error?.code) detail = `ACL revoke failed: ${body.error.code}`
        } catch {
            // use default
        }
        throw new Error(detail)
    }
}

export async function softDeleteMcpConfigBlob(
    apiBaseUrl: string,
    entry: Pick<McpConnectionEntry, 'configBlobId' | 'readKey' | 'readerSeed' | 'writerSeed'>,
): Promise<void> {
    const secret = buildConfigWorkspaceSecret(entry)
    const authProvider = createWorkspaceAuthProvider(apiBaseUrl, encodeWorkspaceSecretV2(secret), entry.configBlobId)
    if (!authProvider) {
        throw new Error(`Failed to create config auth provider for '${entry.configBlobId}'.`)
    }

    const remote = new HttpRemoteStore(apiBaseUrl, '', entry.configBlobId, authProvider)
    await remote.softDeleteBlob(entry.configBlobId)
}
